/**
 * Two solvers, for two different questions.
 *
 * `findSolutions` answers "is this board unique?" by search. It is the
 * correctness gate: a board with two solutions is not a puzzle.
 *
 * `Deducer` answers "how hard is this board?" by solving it the way a person
 * would — a ladder of named techniques, and the board is scored by how far up it
 * had to reach and how often. That is the only reason the second solver exists: a
 * search can tell you a board is solvable, but not whether a human could get
 * there without guessing. Each rung is sound on its own, so a rating is never an
 * artefact of one path the solver happened to take.
 *
 * The ladder:
 *   1. Counting     — a full unit crosses out its rest; a tight unit stars its
 *                     rest; a star crosses out its eight neighbours.
 *   2. Confinement  — one unit's remaining stars all sit inside another unit
 *                     that needs the same number, so that unit's other cells go.
 *   3. Set logic    — the same idea for groups of up to three units: if k rows'
 *                     worth of stars must land in k regions, nothing else in
 *                     those regions can hold one.
 *   4. Enumeration  — list every legal way to finish one unit; anything true of
 *                     all of them is true. This covers the "wherever this
 *                     region's stars go, they cover that cell" deductions.
 *   5. Contradiction— assume a cell, run rungs 1–4, and take the contradiction.
 *                     One level deep, which is the "expert" tier.
 */

import { FAMILY_COUNT, NEIGHBORS, buildGeometry, type Geometry } from './board';
import { CELLS, SIZE, STARS, type Difficulty } from './types';

export const UNKNOWN = 0;
export const S_STAR = 1;
export const S_DOT = 2;

// ── Human-style deduction ───────────────────────────────────────────────────

export type Technique = 1 | 2 | 3 | 4 | 5;

/**
 * What acting on a deduction gets the player.
 *
 * The hint search ranks by this before anything else, because a person does not
 * play the way the solver does. The solver is happy to sweep the whole grid and
 * shave a cell off six different units; a person wants one visible step — one
 * square they can cross out, or a region that closes — and then to look at the
 * board again.
 *
 * The one-square cases are split by which mark they settle because they are not
 * the same move. "One square there can be crossed out" is the smallest, safest
 * thing a hint can offer: the player checks one square, makes one mark, and has
 * lost nothing if they misread it. So it is what the search reaches for first,
 * and the message says outright which mark is coming rather than leaving the
 * player to work out what "a single square" was supposed to mean.
 */
export type DeductionShape =
  /** Places a unit's last stars, so that region, row or column is done. */
  | 'completes'
  /** Settles exactly one square the player has not marked, and it is a cross. */
  | 'one-cross'
  /** Settles exactly one square the player has not marked, and it is a star. */
  | 'one-star'
  | 'plain';

/**
 * How the player is meant to see it, which is not the same as which rung found it.
 *
 * Rung 4 is three different readings wearing one number. Two of them are things
 * a person takes in at a glance — a region that fits together one way, a region
 * whose every fit uses the same square — and the third is a machine sweep over
 * every seating of a unit with up to `ENUMERATION_LIMIT` candidates. Telling a
 * player to "work through every way its stars could sit" hands them that sweep,
 * which is exactly the work nobody wants to do by hand; so the general reading
 * is only ever offered as a claim about one square, and the two glance-sized
 * ones say what they actually look like. The hint carries this rather than the
 * rung for that reason.
 */
export type Approach =
  /** Rung 1: what is left in the unit adds up on its own. */
  | 'count'
  /** Rung 2: the unit's stars are penned inside a line or region it crosses. */
  | 'overlap'
  /** Rung 3: the same, for two or three units taken together. */
  | 'group'
  /** Rung 4 on a glance-sized unit: its stars fit one way and one way only. */
  | 'only-fit'
  /** Rung 4 on a glance-sized unit: every way they fit uses the same square. */
  | 'shared-square'
  /** Rung 4 in general: a square that would leave the unit's stars nowhere. */
  | 'no-room'
  /** Rung 5: assume a square and follow it until something breaks. */
  | 'trial';

/**
 * What the reasoning behind a deduction was about, so a hint can teach it.
 *
 * The approach says what kind of thinking it takes; this says what that
 * thinking was applied to — which line a region's stars are penned into, which
 * rows were taken together, what a trial supposed and what it ran into. A hint
 * that can name those is explaining the move, where one that cannot is only
 * pointing.
 */
export type Detail =
  /** Rung 1: the unit needs `need` stars and has `open` squares left for them. */
  | { kind: 'count'; need: number; open: number }
  /** Rung 2: `from`'s remaining stars, `need` of them, all lie inside the unit. */
  | { kind: 'overlap'; from: number; need: number }
  /**
   * Rung 3: `members`, taken together, need `need` stars, and every open square
   * they have lies in `targets`, which need exactly as many.
   */
  | { kind: 'group'; members: number[]; targets: number[]; need: number }
  /** Rung 4: the unit needs `need` stars among `open` squares. */
  | { kind: 'fit'; need: number; open: number }
  /**
   * Rung 5: supposing a star (or a cross) on a square in the unit broke the
   * board, and `breaks` is the unit that ran out of room, when one did.
   */
  | { kind: 'trial'; star: boolean; breaks: number | null };

export interface NextDeduction {
  unit: number;
  /**
   * How to word it. The rung is not carried alongside: `Approach` already fixes
   * it, and the hint has nothing to say about a rung number anyway.
   */
  approach: Approach;
  shape: DeductionShape;
  detail: Detail;
  /**
   * Whether what it settles lies outside the unit. Rung 4 can cross out a
   * square beside a unit — one its stars touch however they sit — and the hint
   * has to say so, since the square is then not among the ones lit up.
   */
  outside: boolean;
}

/** One unit's worth of deduction, and the cells it would settle. */
interface Lead {
  unit: number;
  stars: number[];
  dots: number[];
  /** Places the unit's last stars, so the unit is finished. */
  completes: boolean;
  /** How to word it — see `Approach`. */
  via: Approach;
  detail: Detail;
}

/** Units are laid out rows, then columns, then regions. */
const FIRST_REGION_UNIT = SIZE * 2;

/**
 * How a rung firing in the general loop is worded.
 *
 * Rung 4's two glance-sized readings never come through here — `forcedLeads`
 * catches those first and words them for what they are — so what is left is the
 * sweep, and 'no-room' is the only honest way to put that to a player: not
 * "enumerate the seatings" but "there is a square here that would leave this
 * unit's stars nowhere to go".
 */
const RUNG_APPROACH: Record<Technique, Approach> = {
  1: 'count',
  2: 'overlap',
  3: 'group',
  4: 'no-room',
  5: 'trial',
};

function shapeOf(lead: Lead): DeductionShape {
  if (lead.completes) return 'completes';
  if (lead.stars.length + lead.dots.length !== 1) return 'plain';
  return lead.dots.length === 1 ? 'one-cross' : 'one-star';
}

function asNext(lead: Lead, geo: Geometry): NextDeduction {
  const member = geo.contains[lead.unit] as Uint8Array;
  return {
    unit: lead.unit,
    approach: lead.via,
    shape: shapeOf(lead),
    detail: lead.detail,
    outside: [...lead.stars, ...lead.dots].some((cell) => member[cell] !== 1),
  };
}

/**
 * Negative when `a` is the better thing to point a player at.
 *
 * One square that can be crossed out first. It is the smallest move on the
 * board and the one a player can check for themselves in a few seconds, which
 * is what a hint is for: it unsticks them without playing the turn, and being
 * one square means there is nothing to hold in their head while they look.
 *
 * Then a finished region, because a region is the shape on the board whose
 * edges a player can see; then one square that has to be a star, which is the
 * same small move but a commitment rather than a note; then a finished line,
 * then whatever is left, regions ahead of lines throughout.
 */
function byUsefulness(a: Lead, b: Lead): number {
  const rank = (lead: Lead): number => {
    const region = lead.unit >= FIRST_REGION_UNIT;
    const shape = shapeOf(lead);
    if (shape === 'one-cross') return 0;
    if (shape === 'completes') return region ? 1 : 3;
    if (shape === 'one-star') return 2;
    return region ? 4 : 5;
  };
  const difference = rank(a) - rank(b);
  if (difference !== 0) return difference;
  // Among equals, the smallest step: fewer cells settled is less to hold in your
  // head, and a hint is meant to unstick a player rather than play for them.
  return a.stars.length + a.dots.length - (b.stars.length + b.dots.length);
}

/**
 * The most useful lead that still leaves the player something to do.
 *
 * Each is trimmed to the cells they have not settled either way before it is
 * ranked, which is both what stops a hint pointing at the ring of crosses they
 * filled in around their own star ten minutes ago, and what makes a one-square
 * shape mean one square *to them*.
 */
function pickLead(leads: readonly Lead[], alreadyKnown: Uint8Array): Lead | null {
  const unknown = (cell: number): boolean => alreadyKnown[cell] !== 1;
  let best: Lead | null = null;
  for (const lead of leads) {
    const fresh: Lead = {
      unit: lead.unit,
      stars: lead.stars.filter(unknown),
      dots: lead.dots.filter(unknown),
      completes: lead.completes,
      via: lead.via,
      detail: lead.detail,
    };
    if (fresh.stars.length + fresh.dots.length === 0) continue;
    if (!best || byUsefulness(fresh, best) < 0) best = fresh;
  }
  return best;
}

export interface DeduceResult {
  solved: boolean;
  contradiction: boolean;
  /** Hardest rung that produced a deduction. */
  hardest: Technique;
  /**
   * How many times each rung fired, indexed by technique.
   *
   * The hardest rung alone does not separate a board that needed one clever step
   * from one that needed twenty, and nearly every board needs rung 4 somewhere —
   * so the rating uses both.
   */
  steps: number[];
}

/** Largest unit candidate list we will enumerate placements for (rung 4). */
const ENUMERATION_LIMIT = 32;
/** Largest unit a person takes in whole — the hint's version of that limit. */
export const GLANCE_LIMIT = 8;
/** Largest unit group considered by set logic (rung 3). */
const SET_LOGIC_MAX = 3;

const UNIT_COUNT = SIZE * FAMILY_COUNT;

const IN_ORDER: readonly number[] = Array.from({ length: UNIT_COUNT }, (_, unit) => unit);
/** The same units, regions first — see `Deducer.order`. */
const REGIONS_FIRST: readonly number[] = [
  ...IN_ORDER.slice(FIRST_REGION_UNIT),
  ...IN_ORDER.slice(0, FIRST_REGION_UNIT),
];

/**
 * `TOUCHES[a * CELLS + b]` is 1 when the two cells touch, diagonals included.
 *
 * The same fact as `NEIGHBORS`, as a lookup rather than a list to scan: the
 * seating enumeration asks it for every pair of cells it considers, and the
 * contradiction search runs that enumeration thousands of times a board.
 */
const TOUCHES: Uint8Array = (() => {
  const table = new Uint8Array(CELLS * CELLS);
  for (let cell = 0; cell < CELLS; cell += 1) {
    for (const neighbor of NEIGHBORS[cell] as number[]) table[cell * CELLS + neighbor] = 1;
  }
  return table;
})();

/**
 * Scratch membership stamps, shared by every `Deducer`.
 *
 * A stamp is only ever read inside the method that wrote it, and no such method
 * hands control to another deducer while it holds one, so one array serves them
 * all. It used to be one per deducer, which put an allocation on every clone —
 * and the contradiction search clones once per square per trial.
 */
const STAMP = new Int32Array(CELLS);
let stampMark = 0;

function freshStamp(): number {
  stampMark += 1;
  return stampMark;
}

export class Deducer {
  readonly geo: Geometry;
  readonly cells: Uint8Array;
  contradiction = false;

  private readonly starIn: Int32Array;
  private readonly unknownIn: Int32Array;
  /** Squares not yet settled, so `solved` is a comparison rather than a scan. */
  private unknownTotal: number;
  private dirty = false;
  /**
   * Which unit's deduction settled each cell, or -1, and the unit currently
   * doing the settling.
   *
   * Only the hint turns this on — `decidedBy` stays null on the solving path so
   * the search does not pay to allocate and fill it on every clone. It records
   * where new marks appeared rather than where the reasoning started, because
   * "row 4 has something to settle" is what a player can act on.
   */
  private decidedBy: Int32Array | null = null;
  private currentUnit = -1;
  /**
   * What the rung doing the settling was reasoning about, and — once the hint
   * turns attribution on — the first such detail for each unit it credited.
   */
  private currentDetail: Detail | null = null;
  private detailOf: Map<number, Detail> | null = null;
  /**
   * The unit a contradiction was found in: the one that ran out of room for
   * its stars, or took too many. A trial hint names it as the thing to watch.
   */
  brokenUnit: number | null = null;
  /**
   * The order the unit-scanning rungs walk their units in.
   *
   * Every rung is sound whichever unit it starts from, so this cannot change
   * whether a board is solvable or what it rates — only which of several equally
   * valid deductions is found first. The hint path sets it to regions-first,
   * because a region is the thing on the board whose edges a player can see; the
   * solving path leaves it alone so ratings stay comparable.
   */
  private order: readonly number[] = IN_ORDER;

  constructor(geo: Geometry, from?: Deducer) {
    this.geo = geo;
    if (from) {
      this.cells = from.cells.slice();
      this.starIn = from.starIn.slice();
      this.unknownIn = from.unknownIn.slice();
      this.unknownTotal = from.unknownTotal;
      this.contradiction = from.contradiction;
    } else {
      this.cells = new Uint8Array(CELLS);
      this.starIn = new Int32Array(UNIT_COUNT);
      this.unknownIn = new Int32Array(UNIT_COUNT);
      this.unknownTotal = CELLS;
      for (let u = 0; u < UNIT_COUNT; u += 1) {
        this.unknownIn[u] = (geo.units[u] as number[]).length;
      }
    }
  }

  clone(): Deducer {
    return new Deducer(this.geo, this);
  }

  need(unit: number): number {
    return STARS - (this.starIn[unit] as number);
  }

  candidates(unit: number): number[] {
    const out: number[] = [];
    for (const cell of this.geo.units[unit] as number[]) {
      if ((this.cells[cell] as number) === UNKNOWN) out.push(cell);
    }
    return out;
  }

  /** Marks a cell, cascading the adjacency rule. Sets `contradiction` on clash. */
  private assign(index: number, value: typeof S_STAR | typeof S_DOT): void {
    const current = this.cells[index] as number;
    if (current === value) return;
    if (current !== UNKNOWN) {
      this.contradiction = true;
      return;
    }
    this.cells[index] = value;
    this.unknownTotal -= 1;
    if (this.decidedBy) {
      this.decidedBy[index] = this.currentUnit;
      if (this.currentDetail && !(this.detailOf as Map<number, Detail>).has(this.currentUnit)) {
        (this.detailOf as Map<number, Detail>).set(this.currentUnit, this.currentDetail);
      }
    }
    this.dirty = true;
    const base = index * FAMILY_COUNT;
    for (let f = 0; f < FAMILY_COUNT; f += 1) {
      const unit = this.geo.unitsOfCell[base + f] as number;
      this.unknownIn[unit] = (this.unknownIn[unit] as number) - 1;
      if (value === S_STAR) this.starIn[unit] = (this.starIn[unit] as number) + 1;
      if (
        (this.starIn[unit] as number) > STARS ||
        (this.starIn[unit] as number) + (this.unknownIn[unit] as number) < STARS
      ) {
        this.contradiction = true;
        this.brokenUnit ??= unit;
      }
    }
    if (value === S_STAR) {
      for (const neighbor of NEIGHBORS[index] as number[]) {
        if ((this.cells[neighbor] as number) === S_STAR) {
          this.contradiction = true;
        } else if ((this.cells[neighbor] as number) === UNKNOWN) {
          this.assign(neighbor, S_DOT);
        }
      }
    }
  }

  place(index: number): void {
    this.assign(index, S_STAR);
  }

  exclude(index: number): void {
    this.assign(index, S_DOT);
  }

  solved(): boolean {
    return this.unknownTotal === 0 && !this.contradiction;
  }

  starCells(): number[] {
    const out: number[] = [];
    for (let cell = 0; cell < CELLS; cell += 1) {
      if ((this.cells[cell] as number) === S_STAR) out.push(cell);
    }
    return out;
  }

  // ── Rung 1: counting ──────────────────────────────────────────────────────

  private counting(): boolean {
    let any = false;
    for (;;) {
      this.dirty = false;
      for (let unit = 0; unit < UNIT_COUNT; unit += 1) {
        if (this.contradiction) return any;
        const unknown = this.unknownIn[unit] as number;
        if (unknown === 0) continue;
        const need = this.need(unit);
        if (need === 0) {
          this.currentUnit = unit;
          for (const cell of this.geo.units[unit] as number[]) {
            if ((this.cells[cell] as number) === UNKNOWN) this.exclude(cell);
          }
        } else if (need === unknown) {
          this.currentUnit = unit;
          for (const cell of this.geo.units[unit] as number[]) {
            if ((this.cells[cell] as number) === UNKNOWN) this.place(cell);
          }
        }
      }
      if (!this.dirty) break;
      any = true;
    }
    return any;
  }

  // ── Rung 2: one unit confined to another ──────────────────────────────────

  private confinement(): boolean {
    for (const u of this.order) {
      const need = this.need(u);
      if (need <= 0) continue;
      const cand = this.candidates(u);
      if (cand.length === 0) continue;
      const mark = freshStamp();
      for (const cell of cand) STAMP[cell] = mark;

      for (const v of this.order) {
        if (v === u || this.need(v) !== need) continue;
        let inside = true;
        for (const cell of cand) {
          if ((this.geo.contains[v] as Uint8Array)[cell] !== 1) {
            inside = false;
            break;
          }
        }
        if (!inside) continue;
        let changed = false;
        this.currentUnit = v;
        this.currentDetail = { kind: 'overlap', from: u, need };
        for (const cell of this.geo.units[v] as number[]) {
          if ((this.cells[cell] as number) === UNKNOWN && STAMP[cell] !== mark) {
            this.exclude(cell);
            changed = true;
          }
        }
        if (changed) return true;
      }
    }
    return false;
  }

  // ── Rung 3: groups of units confined to groups of units ───────────────────

  private setLogic(): boolean {
    for (let familyA = 0; familyA < FAMILY_COUNT; familyA += 1) {
      const pool: number[] = [];
      for (let i = 0; i < SIZE; i += 1) {
        const unit = familyA * SIZE + i;
        if (this.need(unit) > 0) pool.push(unit);
      }
      for (let familyB = 0; familyB < FAMILY_COUNT; familyB += 1) {
        if (familyB === familyA) continue;
        if (this.setLogicPair(pool, familyA, familyB)) return true;
        if (this.contradiction) return false;
      }
    }
    return false;
  }

  /**
   * Set logic between one family's units and another's, in bitmasks.
   *
   * A family has `SIZE` units, so "which columns does this group of rows still
   * reach" is a ten-bit mask, and a group's reach is the OR of its members'. That
   * turns every group test into a handful of integer operations, where it used
   * to pool the group's open squares into a set and walk it — and this rung runs
   * inside every trial of the contradiction search, which made it the single
   * largest cost of generating a board. The groups are still visited in the same
   * order and the first one that settles anything still wins, so what the rung
   * concludes is exactly what it concluded before.
   */
  private setLogicPair(pool: readonly number[], familyA: number, familyB: number): boolean {
    const baseA = familyA * SIZE;
    const baseB = familyB * SIZE;
    // For each pooled unit, the units of family B its open squares sit in.
    const reach = new Int32Array(pool.length);
    pool.forEach((unit, index) => {
      let mask = 0;
      for (const cell of this.geo.units[unit] as number[]) {
        if ((this.cells[cell] as number) !== UNKNOWN) continue;
        mask |= 1 << ((this.geo.unitsOfCell[cell * FAMILY_COUNT + familyB] as number) - baseB);
      }
      reach[index] = mask;
    });
    // And the other way: for each unit of family B, the family-A units its open
    // squares sit in, which is what says whether a group leaves it anything to
    // cross out.
    const back = new Int32Array(SIZE);
    const needB = new Int32Array(SIZE);
    for (let v = 0; v < SIZE; v += 1) {
      let mask = 0;
      for (const cell of this.geo.units[baseB + v] as number[]) {
        if ((this.cells[cell] as number) !== UNKNOWN) continue;
        mask |= 1 << ((this.geo.unitsOfCell[cell * FAMILY_COUNT + familyA] as number) - baseA);
      }
      back[v] = mask;
      needB[v] = this.need(baseB + v);
    }

    const consider = (touched: number, members: number, totalNeed: number): boolean => {
      let capacity = 0;
      for (let rest = touched; rest !== 0; rest &= rest - 1) {
        capacity += needB[31 - Math.clz32(rest & -rest)] as number;
      }
      if (capacity < totalNeed) {
        this.contradiction = true;
        return true;
      }
      if (capacity !== totalNeed) return false;
      let changed = false;
      const detail = (): Detail => ({
        kind: 'group',
        members: unitsIn(members, baseA),
        targets: unitsIn(touched, baseB),
        need: totalNeed,
      });
      for (let rest = touched; rest !== 0; rest &= rest - 1) {
        const v = 31 - Math.clz32(rest & -rest);
        if (((back[v] as number) & ~members) === 0) continue;
        const unit = baseB + v;
        this.currentUnit = unit;
        if (this.decidedBy) this.currentDetail = detail();
        for (const cell of this.geo.units[unit] as number[]) {
          if ((this.cells[cell] as number) !== UNKNOWN) continue;
          const owner = (this.geo.unitsOfCell[cell * FAMILY_COUNT + familyA] as number) - baseA;
          if ((members & (1 << owner)) !== 0) continue;
          this.exclude(cell);
          changed = true;
        }
      }
      return changed;
    };

    const recurse = (
      start: number,
      size: number,
      touched: number,
      members: number,
      need: number,
    ): boolean => {
      if (size > 0 && consider(touched, members, need)) return true;
      if (size === SET_LOGIC_MAX) return false;
      for (let i = start; i < pool.length; i += 1) {
        const unit = pool[i] as number;
        if (
          recurse(
            i + 1,
            size + 1,
            touched | (reach[i] as number),
            members | (1 << (unit - baseA)),
            need + this.need(unit),
          )
        ) {
          return true;
        }
      }
      return false;
    };

    return recurse(0, 0, 0, 0, 0);
  }

  // ── Rung 4: everything true of every way to finish a unit ─────────────────

  /**
   * Every legal way to finish one unit from here.
   *
   * A placement is legal when its cells do not touch each other and none of the
   * rows, columns, or regions it lands in would overflow. That is a local test —
   * it does not chase the consequences — so a returned placement can still turn
   * out impossible. It never misses a real one, which is what the callers need:
   * rung 4 only concludes what holds for *all* of them, and the search below
   * branches on them and lets propagation sort out the rest.
   */
  placementsForUnit(unit: number): number[][] {
    const need = this.need(unit);
    const cand = this.candidates(unit);
    const out: number[][] = [];
    if (need <= 0 || cand.length < need) return out;

    const chosen: number[] = [];
    const fits = (cell: number): boolean => {
      for (const other of chosen) {
        if (TOUCHES[cell * CELLS + other] === 1) return false;
      }
      const base = cell * FAMILY_COUNT;
      for (let f = 0; f < FAMILY_COUNT; f += 1) {
        const other = this.geo.unitsOfCell[base + f] as number;
        let already = 0;
        for (const picked of chosen) {
          if ((this.geo.unitsOfCell[picked * FAMILY_COUNT + f] as number) === other) already += 1;
        }
        if ((this.starIn[other] as number) + already + 1 > STARS) return false;
      }
      return true;
    };
    const recurse = (start: number): void => {
      if (chosen.length === need) {
        out.push(chosen.slice());
        return;
      }
      for (let i = start; i < cand.length; i += 1) {
        const cell = cand[i] as number;
        if (!fits(cell)) continue;
        chosen.push(cell);
        recurse(i + 1);
        chosen.pop();
      }
    };
    recurse(0);
    return out;
  }

  /** Commits one placement: stars on those cells, crosses on the leftovers. */
  applyPlacement(unit: number, cells: readonly number[]): void {
    for (const cell of cells) this.place(cell);
    for (const cell of this.geo.units[unit] as number[]) {
      if ((this.cells[cell] as number) === UNKNOWN) this.exclude(cell);
    }
  }

  /** The undecided unit with the fewest candidates — the search's branch point. */
  mostConstrainedUnit(): number {
    let best = -1;
    let bestCount = Infinity;
    for (let unit = 0; unit < UNIT_COUNT; unit += 1) {
      if (this.need(unit) <= 0) continue;
      const count = this.unknownIn[unit] as number;
      if (count > 0 && count < bestCount) {
        bestCount = count;
        best = unit;
      }
    }
    return best;
  }

  private enumeration(): boolean {
    const dotVotes = new Int32Array(CELLS);
    const starVotes = new Int32Array(CELLS);

    for (const unit of this.order) {
      if (this.need(unit) <= 0) continue;
      const cand = this.candidates(unit);
      if (cand.length === 0 || cand.length > ENUMERATION_LIMIT) continue;
      const placements = this.placementsForUnit(unit);

      if (placements.length === 0) {
        this.contradiction = true;
        this.brokenUnit ??= unit;
        return false;
      }

      dotVotes.fill(0);
      starVotes.fill(0);
      for (const placement of placements) {
        for (const cell of placement) starVotes[cell] = (starVotes[cell] as number) + 1;
        // Cells this completion would rule out: neighbours of its stars, plus
        // the unit's own leftovers. Counted once each, so a cell reaching the
        // placement count is one every completion rules out. The placement's own
        // stars are stamped first so the leftover pass skips them.
        const mark = freshStamp();
        const starred = freshStamp();
        for (const cell of placement) STAMP[cell] = starred;
        for (const cell of placement) {
          for (const neighbor of NEIGHBORS[cell] as number[]) {
            if ((this.cells[neighbor] as number) !== UNKNOWN) continue;
            if (STAMP[neighbor] === mark) continue;
            STAMP[neighbor] = mark;
            dotVotes[neighbor] = (dotVotes[neighbor] as number) + 1;
          }
        }
        for (const cell of cand) {
          const stamp = STAMP[cell] as number;
          if (stamp === starred || stamp === mark) continue;
          STAMP[cell] = mark;
          dotVotes[cell] = (dotVotes[cell] as number) + 1;
        }
      }

      let changed = false;
      this.currentUnit = unit;
      this.currentDetail = { kind: 'fit', need: this.need(unit), open: cand.length };
      for (let cell = 0; cell < CELLS; cell += 1) {
        if ((this.cells[cell] as number) !== UNKNOWN) continue;
        if ((starVotes[cell] as number) === placements.length) {
          this.place(cell);
          changed = true;
        } else if ((dotVotes[cell] as number) === placements.length) {
          this.exclude(cell);
          changed = true;
        }
      }
      if (changed) return true;
    }
    return false;
  }

  // ── Rung 5: assume, propagate, take the contradiction ─────────────────────

  private contradictionSearch(): boolean {
    for (const trialValue of [S_STAR, S_DOT] as const) {
      const wins: { cell: number; breaks: number | null }[] = [];
      for (let cell = 0; cell < CELLS; cell += 1) {
        if ((this.cells[cell] as number) !== UNKNOWN) continue;
        const trial = this.clone();
        if (trialValue === S_STAR) trial.place(cell);
        else trial.exclude(cell);
        trial.run(4);
        if (trial.contradiction) wins.push({ cell, breaks: trial.brokenUnit });
      }
      if (wins.length > 0) {
        for (const { cell, breaks } of wins) {
          if ((this.cells[cell] as number) !== UNKNOWN) continue;
          // The row the cell sits in: a contradiction is found at a cell, not in
          // a unit, and its row is the smallest thing to send a player looking at.
          this.currentUnit = Math.floor(cell / SIZE);
          this.currentDetail = { kind: 'trial', star: trialValue === S_STAR, breaks };
          if (trialValue === S_STAR) this.exclude(cell);
          else this.place(cell);
        }
        return true;
      }
    }
    return false;
  }

  // ── Driver ────────────────────────────────────────────────────────────────

  private applyRung(rung: Technique): boolean {
    if (rung === 1) return this.counting();
    if (rung === 2) return this.confinement();
    if (rung === 3) return this.setLogic();
    if (rung === 4) return this.enumeration();
    return this.contradictionSearch();
  }

  private resetAttribution(): void {
    if (!this.decidedBy) this.decidedBy = new Int32Array(CELLS);
    this.decidedBy.fill(-1);
    this.currentUnit = -1;
    this.currentDetail = null;
    this.detailOf = new Map();
  }

  /**
   * Every unit counting fires on right now, and what each would settle.
   *
   * Nothing is applied: the hint picks among these rather than taking the first,
   * and counting is cheap enough to enumerate whole. This is where most of a
   * board's progress lives, so it is also where the choice is worth making.
   */
  private countingLeads(): Lead[] {
    const leads: Lead[] = [];
    for (let unit = 0; unit < UNIT_COUNT; unit += 1) {
      const unknown = this.unknownIn[unit] as number;
      if (unknown === 0) continue;
      const need = this.need(unit);
      if (need !== 0 && need !== unknown) continue;
      const open: number[] = [];
      for (const cell of this.geo.units[unit] as number[]) {
        if ((this.cells[cell] as number) === UNKNOWN) open.push(cell);
      }
      leads.push({
        unit,
        stars: need === 0 ? [] : open,
        dots: need === 0 ? open : [],
        // The `need === unknown` branch: these are the unit's last stars.
        completes: need > 0,
        via: 'count',
        detail: { kind: 'count', need, open: open.length },
      });
    }
    return leads;
  }

  /**
   * Units small enough to take in whole whose seatings already settle something.
   *
   * Two kinds, and a person reads both off the shape without counting:
   *
   *   - **One seating.** The unit fits together one way, so its stars are placed
   *     and the rest of it is crossed. The unit is finished on sight.
   *   - **A square every seating uses.** More than one way to sit, but they all
   *     put a star on the same square, so that square is a star whatever else
   *     turns out to be true. The 2×3 rectangle laid across two rows is the
   *     shape this is for, and it is the second of the openings `regionGift`
   *     counts when it decides a board is easy.
   *
   * Rung 4 finds both, but only after rungs 2 and 3 have been tried, and that
   * order is about what costs the *solver* least. A three-cell region that fits
   * together one way is the easiest thing on a board to a person and among the
   * last things the ladder reaches for, so the hint looks here before it looks
   * at confinement. Leaving the second kind out was what let a hint answer a
   * board built around a forced square with set logic over three units — sound,
   * and far past what the board was asking of the player. Bounded by what
   * someone will eye up without counting on their fingers, which is the only
   * reason this is a hint and not a rung.
   */
  private forcedLeads(): Lead[] {
    const leads: Lead[] = [];
    for (const unit of this.order) {
      if (this.need(unit) <= 0) continue;
      const cand = this.candidates(unit);
      if (cand.length === 0 || cand.length > GLANCE_LIMIT) continue;
      const placements = this.placementsForUnit(unit);
      if (placements.length === 0) continue;
      if (placements.length === 1) {
        const stars = placements[0] as number[];
        leads.push({
          unit,
          stars: [...stars],
          dots: cand.filter((cell) => !stars.includes(cell)),
          completes: true,
          via: 'only-fit',
          detail: { kind: 'fit', need: this.need(unit), open: cand.length },
        });
        continue;
      }
      const shared = (placements[0] as number[]).filter((cell) =>
        placements.every((placement) => placement.includes(cell)),
      );
      // Never all of them: placements agreeing on every star would be one
      // placement, which the branch above already took.
      if (shared.length > 0) {
        leads.push({
          unit,
          stars: shared,
          dots: [],
          completes: false,
          via: 'shared-square',
          detail: { kind: 'fit', need: this.need(unit), open: cand.length },
        });
      }
    }
    return leads;
  }

  /** One unit's worth of counting, so a caller can watch it a step at a time. */
  private countingStep(): boolean {
    for (let unit = 0; unit < UNIT_COUNT; unit += 1) {
      const unknown = this.unknownIn[unit] as number;
      if (unknown === 0) continue;
      const need = this.need(unit);
      if (need !== 0 && need !== unknown) continue;
      this.currentUnit = unit;
      this.currentDetail = { kind: 'count', need, open: unknown };
      for (const cell of this.geo.units[unit] as number[]) {
        if ((this.cells[cell] as number) !== UNKNOWN) continue;
        if (need === 0) this.exclude(cell);
        else this.place(cell);
      }
      return true;
    }
    return false;
  }

  /**
   * The next deduction the player has not already made, for the hint button.
   *
   * `alreadyKnown` marks the cells they have settled either way. Without it the
   * hint is worthless in the common case: the solver is seeded from their stars
   * alone, so the first thing it "discovers" is usually the crosses they filled
   * in around those stars ten minutes ago. So each step is taken, checked for
   * whether it settles anything new to them, and — when it does not — absorbed,
   * bringing the solver's knowledge up to theirs before looking further.
   *
   * What comes back is the easiest thing left to notice, in a person's order
   * rather than the ladder's: everything they could see without working for it
   * — counting, and the units small enough to take in whole — and then the rungs
   * as they come. Those two are ranked against each other rather than one being
   * preferred wholesale, because both are things a player *sees*, so which one
   * to offer is a question about the payoff and not about difficulty; and
   * `byUsefulness` answers it by reaching for one square that can be crossed out
   * before anything bigger. It reports the unit and how to look at it, never the
   * cells, which is what keeps a hint a nudge rather than the answer.
   */
  nextDeduction(alreadyKnown: Uint8Array): NextDeduction | null {
    const state = this.clone();
    state.order = REGIONS_FIRST;
    // Generous, but finite: every step settles at least one cell, so the board
    // itself bounds this long before the guard does.
    for (let step = 0; step < CELLS * 2; step += 1) {
      if (state.contradiction || state.solved()) return null;

      // Everything on offer at a glance: every unit counting fires on, and every
      // unit small enough that its seatings can be read off its shape. Which of
      // them the player is sent to is the whole difference between a hint that
      // crosses off one square and one that shaves a cell off each of six lines.
      const seen = [...state.countingLeads(), ...state.forcedLeads()];
      const easy = pickLead(seen, alreadyKnown);
      if (easy) return asNext(easy, state.geo);

      state.resetAttribution();
      let technique: Technique | null = null;
      if (state.countingStep()) {
        // Nothing new to the player in it — absorb it and look further.
        technique = 1;
      } else {
        for (const rung of [2, 3, 4, 5] as const) {
          if (state.applyRung(rung)) {
            technique = rung;
            break;
          }
        }
      }
      if (technique === null || state.contradiction) return null;
      if (technique === 1) continue;

      const lead = pickLead(state.leadsFromAttribution(RUNG_APPROACH[technique]), alreadyKnown);
      if (lead) return asNext(lead, state.geo);
    }
    return null;
  }

  /** What the rung just applied settled, grouped by the unit it credited. */
  private leadsFromAttribution(via: Approach): Lead[] {
    const settled = this.decidedBy as Int32Array;
    const byUnit = new Map<number, Lead>();
    for (let cell = 0; cell < CELLS; cell += 1) {
      const unit = settled[cell] as number;
      if (unit < 0) continue;
      let lead = byUnit.get(unit);
      if (!lead) {
        const detail = this.detailOf?.get(unit) ?? {
          kind: 'count',
          need: this.need(unit),
          open: (this.unknownIn[unit] as number) + 1,
        };
        lead = { unit, stars: [], dots: [], completes: false, via, detail };
        byUnit.set(unit, lead);
      }
      if ((this.cells[cell] as number) === S_STAR) lead.stars.push(cell);
      else lead.dots.push(cell);
    }
    for (const lead of byUnit.values()) {
      lead.completes = lead.stars.length > 0 && this.need(lead.unit) === 0;
    }
    return [...byUnit.values()];
  }

  /**
   * Rung 5 the way a person does it: one square at a time, nearest first.
   *
   * The rung proper tries every square and takes every contradiction it finds,
   * which is what keeps ratings independent of order. A person stops at the
   * first, and starts looking beside what they did last, so this takes the
   * squares in order of distance from `focus` and applies the first that breaks.
   */
  private contradictionNear(focus: readonly number[]): boolean {
    const open: number[] = [];
    for (let cell = 0; cell < CELLS; cell += 1) {
      if ((this.cells[cell] as number) === UNKNOWN) open.push(cell);
    }
    const gap = (cell: number): number => nearestGap(cell, focus);
    open.sort((a, b) => gap(a) - gap(b) || a - b);
    for (const cell of open) {
      for (const trialValue of [S_STAR, S_DOT] as const) {
        const trial = this.clone();
        if (trialValue === S_STAR) trial.place(cell);
        else trial.exclude(cell);
        trial.run(4);
        if (!trial.contradiction) continue;
        if (trialValue === S_STAR) this.exclude(cell);
        else this.place(cell);
        return true;
      }
    }
    return false;
  }

  /**
   * Solves a copy of this board the way a person follows a thread, counting
   * how often the next step was right beside the last. See `flowOf`.
   */
  followThread(): { steps: number; followed: number } | null {
    const state = this.clone();
    const settledSince = (before: Uint8Array): number[] => {
      const out: number[] = [];
      for (let cell = 0; cell < CELLS; cell += 1) {
        if ((before[cell] as number) === UNKNOWN && (state.cells[cell] as number) !== UNKNOWN) {
          out.push(cell);
        }
      }
      return out;
    };

    let last: number[] = [];
    let steps = 0;
    let followed = 0;
    state.counting();
    for (let guard = 0; guard < CELLS * 2; guard += 1) {
      if (state.contradiction) return null;
      if (state.solved()) break;
      const before = state.cells.slice();
      state.order = unitsNearest(state.geo, last);
      let fired = false;
      for (const rung of [2, 3, 4] as const) {
        if (state.applyRung(rung)) {
          fired = true;
          break;
        }
      }
      if (!fired && !state.contradictionNear(last)) return null;
      if (state.contradiction) return null;
      const fresh = settledSince(before);
      if (last.length > 0) {
        steps += 1;
        if (fresh.some((cell) => nearestGap(cell, last) <= 1)) followed += 1;
      }
      // What the player sees happen next is the step and every star the
      // counting that follows it drops in; the crosses that sweep a finished
      // line are bookkeeping, and counting them would call everything nearby.
      const beforeCascade = state.cells.slice();
      state.counting();
      const stars = settledSince(beforeCascade).filter(
        (cell) => (state.cells[cell] as number) === S_STAR,
      );
      last = [...fresh, ...stars];
    }
    return state.solved() ? { steps, followed } : null;
  }

  /** Runs the ladder up to `maxTechnique`, returning the hardest rung used. */
  run(maxTechnique: Technique): DeduceResult {
    let hardest: Technique = 1;
    const steps = [0, 0, 0, 0, 0, 0];
    const fired = (rung: Technique): void => {
      steps[rung] = (steps[rung] as number) + 1;
      if (hardest < rung) hardest = rung;
    };
    for (;;) {
      if (this.contradiction) break;
      if (this.counting()) {
        fired(1);
        continue;
      }
      if (this.contradiction || this.solved()) break;
      if (maxTechnique >= 2 && this.confinement()) {
        fired(2);
        continue;
      }
      if (maxTechnique >= 3 && this.setLogic()) {
        fired(3);
        continue;
      }
      if (this.contradiction) break;
      if (maxTechnique >= 4 && this.enumeration()) {
        fired(4);
        continue;
      }
      if (this.contradiction) break;
      if (maxTechnique >= 5 && this.contradictionSearch()) {
        fired(5);
        continue;
      }
      break;
    }
    return { solved: this.solved(), contradiction: this.contradiction, hardest, steps };
  }
}

/** The units a family's bitmask names, as unit indices from `base`. */
function unitsIn(mask: number, base: number): number[] {
  const out: number[] = [];
  for (let rest = mask; rest !== 0; rest &= rest - 1)
    out.push(base + 31 - Math.clz32(rest & -rest));
  return out;
}

/** Squares between two cells, counting diagonal steps as one. */
function chebyshev(a: number, b: number): number {
  return Math.max(
    Math.abs(Math.floor(a / SIZE) - Math.floor(b / SIZE)),
    Math.abs((a % SIZE) - (b % SIZE)),
  );
}

/** How far a cell is from the nearest of `cells`; infinite when there are none. */
function nearestGap(cell: number, cells: readonly number[]): number {
  let best = Number.POSITIVE_INFINITY;
  for (const other of cells) best = Math.min(best, chebyshev(cell, other));
  return best;
}

/** Every unit, the ones with an open square nearest `cells` first. */
function unitsNearest(geo: Geometry, cells: readonly number[]): number[] {
  if (cells.length === 0) return [...IN_ORDER];
  const gap = IN_ORDER.map((unit) => {
    let best = Number.POSITIVE_INFINITY;
    for (const cell of geo.units[unit] as number[]) best = Math.min(best, nearestGap(cell, cells));
    return best;
  });
  return [...IN_ORDER].sort((a, b) => (gap[a] as number) - (gap[b] as number) || a - b);
}

// ── Uniqueness ──────────────────────────────────────────────────────────────

/**
 * Finds up to `limit` solutions.
 *
 * Propagate, then branch on the unit with the fewest candidates left, then
 * propagate again. Branching on a *unit* rather than a cell is what makes this
 * quick: a region with four candidates and two stars has at most a handful of
 * completions, and each one decides several cells at once instead of one.
 *
 * The generator wants the *second* solution, not just its existence: knowing
 * which extra star a rival solution uses turns "this board is ambiguous" into
 * "move that cell into another region and it stops being ambiguous".
 */
export function findSolutions(regions: readonly number[], limit = 2): number[][] {
  const geo = buildGeometry(regions);
  const found: number[][] = [];

  const search = (state: Deducer): void => {
    if (found.length >= limit) return;
    // Rung 1 only: measured against rungs 2–4, the extra pruning per node never
    // pays for what it costs to compute at every node of the tree.
    state.run(1);
    if (state.contradiction) return;
    if (state.solved()) {
      found.push(state.starCells());
      return;
    }
    const unit = state.mostConstrainedUnit();
    if (unit < 0) return;
    for (const placement of state.placementsForUnit(unit)) {
      const trial = state.clone();
      trial.applyPlacement(unit, placement);
      search(trial);
      if (found.length >= limit) return;
    }
  };

  search(new Deducer(geo));
  return found;
}

export function countSolutions(regions: readonly number[], limit = 2): number {
  return findSolutions(regions, limit).length;
}

// ── Rating ──────────────────────────────────────────────────────────────────

/**
 * Effort score: how much work past plain counting the board demanded.
 *
 * Weights rise steeply because the rungs do. A confinement is something a player
 * spots in passing; a contradiction search means picking a cell, holding a
 * hypothetical board in your head, and following it to a clash.
 */
export function effortScore(result: DeduceResult): number {
  const raw =
    1 * (result.steps[2] as number) +
    3 * (result.steps[3] as number) +
    7 * (result.steps[4] as number) +
    40 * (result.steps[5] as number);
  return raw / SIZE;
}

/**
 * Score cut-offs per difficulty, measured rather than guessed.
 *
 * `__bench/calibrate.ts` samples unique boards and prints the quartiles of
 * `effortScore`; these are those quartiles, rounded. Rerun it after changing the
 * ladder or how regions are grown — both move the distribution, and stale
 * cut-offs would file every board under one label.
 *
 * They are quartiles of what this board is, not an absolute scale. Nearly every
 * two-star 10×10 needs the contradiction search somewhere, so the gentlest
 * quarter is a long way from a board with no hard steps in it — which is why
 * `gradeBoard` no longer ships that quarter as "easy" and `CUTS.easy` now marks
 * the floor of medium instead.
 *
 * Rechecked when the generator went symmetric: 120 symmetric boards came out at
 * 7.7, 9.3 and 12.1, which is within what sixty samples wander by, so the cuts
 * stayed where they were.
 */
const CUTS = { easy: 8.2, medium: 9.6, hard: 11.8 };

function difficultyFor(score: number): Difficulty {
  if (score <= CUTS.easy) return 'easy';
  if (score <= CUTS.medium) return 'medium';
  if (score <= CUTS.hard) return 'hard';
  return 'expert';
}

export interface BoardRating {
  difficulty: Difficulty;
  score: number;
  hardest: Technique;
}

/**
 * How a board plays, or null when even rung 5 stalls.
 *
 * Null is a rejection, not an "extra hard": a board nobody can finish without
 * guessing is not one we should ship, however unique its solution is.
 *
 * The whole ladder always runs. A single contradiction search costs 40/SIZE = 4,
 * which is below even the "easy" cut-off, so there is no target for which a
 * rung-5 board can be ruled out early — stopping short would only mis-rate it.
 */
export function rateBoard(
  regions: readonly number[],
  maxTechnique: Technique = 5,
): BoardRating | null {
  const deducer = new Deducer(buildGeometry(regions));
  const result = deducer.run(maxTechnique);
  if (!result.solved || result.contradiction) return null;
  const score = effortScore(result);
  return { difficulty: difficultyFor(score), score, hardest: result.hardest };
}

// ── Flow ────────────────────────────────────────────────────────────────────

/**
 * How often a board's next step sits right beside its last one, from 0 to 1,
 * or null when the ladder cannot finish it.
 *
 * The thing being measured is whether a board *leads* the player: after each
 * deduction, is there another one touching the squares it just settled — the
 * consequence of a move sitting next to the move — or do they have to leave
 * and scan the grid for the next way in? The board is solved the way a person
 * follows a thread: the units nearest what just happened are looked at first,
 * and a trial starts beside it too. Which step is taken never changes whether
 * a board is solvable or what it rates, only whether there was something
 * nearby to take, which is what this counts.
 *
 * "Beside" is a king's move, the same neighbourhood the no-touching rule is
 * written in: a star's crosses are beside it, and so is the square that can
 * only now be seen to hold a star because of them.
 */
export function flowOf(regions: readonly number[]): number | null {
  const thread = new Deducer(buildGeometry(regions)).followThread();
  if (!thread) return null;
  return thread.steps === 0 ? 1 : thread.followed / thread.steps;
}

/**
 * How much of a board's opening has no mirror image, from 0 to 1.
 *
 * The opening is everything the easiest rung that gets anywhere on an empty
 * board settles, run to a standstill. The rungs only conclude what follows and
 * a standstill does not depend on the order they ran in, so on a board that is
 * a half turn of itself the opening is too: every square it settles, it settles
 * the turn of as well. On a nearly symmetric board it need not be, and the
 * squares it settles whose turn it does not are the ones the difference between
 * the halves paid for — a first move that falls out of the one square that is
 * not where the mirror says. This is the share of the opening that is.
 *
 * The rungs stop at 4: a board whose first move is a trial has no opening to
 * speak of, and running the contradiction search just to score one would cost
 * more than every other measure put together.
 */
export function openingFromDifference(regions: readonly number[]): number {
  const geo = buildGeometry(regions);
  for (const rung of [1, 2, 3, 4] as const) {
    const deducer = new Deducer(geo);
    deducer.run(rung);
    if (deducer.contradiction) return 0;
    let settled = 0;
    let unmatched = 0;
    for (let cell = 0; cell < CELLS; cell += 1) {
      if ((deducer.cells[cell] as number) === UNKNOWN) continue;
      settled += 1;
      if ((deducer.cells[CELLS - 1 - cell] as number) === UNKNOWN) unmatched += 1;
    }
    if (settled > 0) return unmatched / settled;
  }
  return 0;
}

// ── Regions a player can open on sight ──────────────────────────────────────

/**
 * The largest region whose seatings a player reads off the map rather than
 * works out.
 *
 * "Every seating of this region uses that square" is only something a person
 * *sees* while the region is small enough to take in whole. Six is the 2×3
 * rectangle, the biggest shape in the set below; at twelve it is a search, and
 * calling that a gift would count regions the player never gets anything free
 * from.
 */
const GIVEN_MAX_CELLS = 6;

/**
 * What a region hands over the moment the board appears.
 *
 * An `opening` settles something: a star goes in, or a whole line's allowance is
 * claimed. A `nudge` does not go that far — no star, but squares that can be
 * crossed out on sight, which is still a foothold and still free.
 */
export type Gift = 'opening' | 'nudge';

/** What each is worth toward a tier's band. A nudge is half a way in. */
export const GIFT_VALUE: Record<Gift, number> = { opening: 1, nudge: 0.5 };

/** The seatings of a region: pairs of its cells that two stars could take. */
function seatingsOf(cells: readonly number[]): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < cells.length; i += 1) {
    for (let j = i + 1; j < cells.length; j += 1) {
      const first = cells[i] as number;
      const second = cells[j] as number;
      if (!(NEIGHBORS[first] as number[]).includes(second)) out.push([first, second]);
    }
  }
  return out;
}

/**
 * What one region gives away the moment the board appears, or null.
 *
 * Five shapes qualify, and all of them are things a person spots without
 * searching. Four are openings:
 *
 *   - **One seating.** A region whose two stars have exactly one legal placement
 *     hands over both stars outright. Every 3-cell region is one of these — two
 *     stars cannot touch, so a 3-cell region is always a straight line with the
 *     stars at its ends — and the 4-cell T and S are too.
 *   - **A forced star.** A small region with several seatings that all use the
 *     same square: that square is a star, whatever happens to the other. Two
 *     shapes carry this — the 2×2 block with a cell hanging off it, and the L,
 *     which places its corner star and leaves two squares for the second.
 *   - **A line.** A region lying entirely inside one row or column takes that
 *     line's whole allowance of stars, so the rest of the line is crossed out on
 *     sight even while where the stars sit inside the region is still open.
 *
 * And one is a nudge:
 *
 *   - **A dead square.** A small region holding a square no seating can use —
 *     one that touches every other square in the region. The 2×3 rectangle is
 *     the shape this is for: both middles are dead, which crosses them out, puts
 *     one star in each short end, and takes the squares beside those ends with
 *     it. No star placed, but the region has stopped being a blank.
 *
 * This is a structural property, read straight off the map. That is the point:
 * it is what the *player* sees, where `effortScore` is what the solver had to
 * spend, and the two are not the same thing on a board where twenty cells are
 * free and the rest is a grind.
 */
export function regionGift(cells: readonly number[]): Gift | null {
  if (cells.length === 0) return null;
  const rows = new Set(cells.map((cell) => Math.floor(cell / SIZE)));
  const columns = new Set(cells.map((cell) => cell % SIZE));
  if (rows.size === 1 || columns.size === 1) return 'opening';

  // Only the one-seating rule survives on a region too big to take in whole, and
  // that needs no more than the first two seatings — which is what keeps this
  // cheap on the big regions it is called on for every board.
  if (cells.length > GIVEN_MAX_CELLS) {
    let seatings = 0;
    for (let i = 0; i < cells.length; i += 1) {
      for (let j = i + 1; j < cells.length; j += 1) {
        const first = cells[i] as number;
        if ((NEIGHBORS[first] as number[]).includes(cells[j] as number)) continue;
        seatings += 1;
        if (seatings > 1) return null;
      }
    }
    return seatings === 1 ? 'opening' : null;
  }

  const seatings = seatingsOf(cells);
  if (seatings.length === 0) return null; // no way to seat two stars: not a region
  if (seatings.length === 1) return 'opening';
  const forced = seatings.reduce<number[]>(
    (shared, [first, second]) => shared.filter((cell) => cell === first || cell === second),
    [...(seatings[0] as [number, number])],
  );
  if (forced.length > 0) return 'opening';
  const seated = new Set(seatings.flat());
  return cells.some((cell) => !seated.has(cell)) ? 'nudge' : null;
}

/** Which regions of a map are gifts, in region order. */
export function givenRegions(regions: readonly number[]): number[] {
  const cellsOf: number[][] = Array.from({ length: SIZE }, () => []);
  for (let cell = 0; cell < CELLS; cell += 1) {
    (cellsOf[regions[cell] as number] as number[]).push(cell);
  }
  const out: number[] = [];
  for (let region = 0; region < SIZE; region += 1) {
    if (regionGift(cellsOf[region] as number[])) out.push(region);
  }
  return out;
}

/**
 * How much of a way in a board comes with, counting nudges as half.
 *
 * The number the tiers are set in: a board of five regions that place stars and
 * one of four plus two 2×3 rectangles both come to five, and both open the same
 * amount of the grid before the player has to work.
 */
export function givenValue(regions: readonly number[]): number {
  const cellsOf: number[][] = Array.from({ length: SIZE }, () => []);
  for (let cell = 0; cell < CELLS; cell += 1) {
    (cellsOf[regions[cell] as number] as number[]).push(cell);
  }
  let total = 0;
  for (let region = 0; region < SIZE; region += 1) {
    const gift = regionGift(cellsOf[region] as number[]);
    if (gift) total += GIFT_VALUE[gift];
  }
  return total;
}

/**
 * How much of a way in each tier is built around, as an inclusive range.
 *
 * Measured in `givenValue`, so a band of five is five regions that place a star
 * or six that mix in a couple of 2×3 rectangles. The generator picks a number in
 * the band and draws to it; `gradeBoard` reads the value back off the finished
 * map and holds the tiers to the same line. Easy hands over five or six of the
 * ten regions' worth, which is most of the opening done for you; medium leaves
 * three or four as a way in; hard gives at most one, and expert none.
 */
export const GIVEN_BANDS: Record<Difficulty, readonly [number, number]> = {
  easy: [5, 6],
  medium: [3, 4],
  hard: [0, 1],
  expert: [0, 0],
};

/**
 * What a board plays like, or null when even rung 5 stalls.
 *
 * The easier tiers are *built*, not measured. Rating a board by how far up the
 * ladder the solver had to reach says nothing about the thing that actually
 * stops a person — a full grid with no way in — because a solver is happy to run
 * the whole board through set logic and a person is not. So a board with regions
 * whose stars go in on sight is easy on that ground alone: there is somewhere to
 * start, the first marks come for free, and everything after them has more to
 * work from. A board without them is not easy however gently it scores, which is
 * why the ladder's own "easy" tier now comes back as medium.
 *
 * The gifts also cap a board from above. Half the regions filled in before the
 * first deduction is not hard, whatever the ladder then has to spend on what is
 * left, so a board over the medium band cannot come back harder than medium —
 * short of the ladder failing to finish it gently at all, where the count still
 * pulls it down a tier rather than off the top.
 *
 * The solver still decides everything the count leaves open, and still has the
 * last word on whether a board is shippable at all: null means it needs a guess,
 * and no amount of open regions makes that a puzzle. Over the easy tier it keeps
 * a veto but loses its vote — "several regions free" and "the rest of it is a
 * grind" can both be true of one map, and the grind is mostly rung 4, which is
 * the exhaustive search a person is worst at. What it can no longer do is call
 * a board easy for want of anything to hold against it.
 */
export function gradeBoard(
  regions: readonly number[],
  rated: BoardRating | null | undefined = undefined,
): Difficulty | null {
  const rating = rated === undefined ? rateBoard(regions) : rated;
  if (!rating) return null;
  const given = givenValue(regions);
  const gentle = rating.difficulty === 'easy' || rating.difficulty === 'medium';
  if (given >= GIVEN_BANDS.easy[0] && gentle) return 'easy';
  // Enough of a way in for medium, and the ladder agrees it is not expert work.
  if (given >= GIVEN_BANDS.medium[0] && rating.difficulty !== 'expert') return 'medium';
  if (rating.difficulty === 'easy') return 'medium';
  // Two regions solved before the clock starts is not what expert should mean.
  if (rating.difficulty === 'expert' && given > GIVEN_BANDS.hard[1]) return 'hard';
  return rating.difficulty;
}
